import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSidebarConversations } from '../../../components/layout/useSidebarConversations';
import { useNotification } from '../../../hooks/useNotification';
import type { Project } from '../types';
import { useProjects } from './useProjects';

/** Moves a conversation into a project (or out of it when project is null) with feedback. */
export const useMoveConversation = () => {
  const { t } = useTranslation();
  const { success } = useNotification();
  const { moveConversationToProject, sessions } = useSidebarConversations();
  const { refreshProjects } = useProjects();

  return useCallback(
    async (sessionId: string, project: Pick<Project, 'publicId' | 'name'> | null) => {
      const session = sessions.find((item) => item.id === sessionId);
      const currentProjectId = session?.projectPublicId ?? null;
      const targetProjectId = project?.publicId ?? null;
      if (session && currentProjectId === targetProjectId) return true;

      const moved = await moveConversationToProject(sessionId, project);
      if (!moved) return false;

      void refreshProjects();
      success(
        project
          ? t('projects.notifications.moved', { name: project.name })
          : t('projects.notifications.removed')
      );
      return true;
    },
    [moveConversationToProject, refreshProjects, sessions, success, t]
  );
};
